import { STEPPER_GLOBAL_OPTIONS } from '@angular/cdk/stepper';
import { registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClient } from '@angular/common/http';
import localeIt from '@angular/common/locales/it';
import localeJa from '@angular/common/locales/ja';
import localeNb from '@angular/common/locales/nb';
import localePl from '@angular/common/locales/pl';
import localeZh from '@angular/common/locales/zh';
import { APP_INITIALIZER, NgModule, Optional, SkipSelf } from '@angular/core';
import { MAT_DATE_FORMATS } from '@angular/material/core';
import {
  MAT_MOMENT_DATE_ADAPTER_OPTIONS,
  MAT_MOMENT_DATE_FORMATS,
} from '@angular/material-moment-adapter';
import { NgMapsCoreModule } from '@ng-maps/core';
import { GOOGLE_MAPS_API_CONFIG } from '@ng-maps/google';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { NgxPopperjsModule, NgxPopperjsPlacements } from 'ngx-popperjs';
import { NOC_ANALYTICS } from 'ngx-shared-services';
import { analytics } from 'noc-analytics';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import {
  AuthHttpInterceptor,
  ErrorInterceptor,
  LoaderInterceptor,
} from './interceptor';
import { CoreRoutingModule } from './routing';
import { UserService } from './service/user.service';
import { AuthService } from './service/auth.service';
import { Locale, Timezone } from '@model';
import {
  CookieService,
  CssService,
  DashboardService,
  DialogService,
  EssService,
  ExcelService,
  ForecastService,
  GlobalAlertService,
  GoogleService,
  GridProgramService,
  I18nService,
  IconsService,
  InlineEditService,
  LoadProfileService,
  LoaderService,
  LocalesService,
  LoginRedirectService,
  ProjectService,
  ResourceService,
  ResultService,
  RouterService,
  ScenarioEventsService,
  ScenarioService,
  SidenavService,
  SiteConstraintService,
  SolarPvService,
  TariffService,
  TimezonesService,
  TreeService,
} from '@service';

registerLocaleData(localeIt, 'it-IT');
registerLocaleData(localeZh, 'zh-TW');
registerLocaleData(localePl, 'pl-PL');
registerLocaleData(localeJa, 'ja-JP');
registerLocaleData(localeNb, 'nb-NO');

export function HttpLoaderFactory(http: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(http, '/assets/i18n/', '.json');
}

const initRouterCheck = (routerService: RouterService) => (): Promise<void> =>
  routerService.initRouterCheck();

const initGoogleApiKey = (google: GoogleService) => (): Observable<string> =>
  google.load();

const initIcon = (icon: IconsService) => (): Promise<void> => icon.load();

const initTimezones =
  (timezone: TimezonesService) => (): Observable<Timezone[]> =>
    timezone.loadTimezones();

const initLocales = (locale: LocalesService) => (): Observable<Locale[]> =>
  locale.setLocales();

const initLoginRedirect =
  (loginRedirect: LoginRedirectService) => (): Promise<void> =>
    loginRedirect.loadLoginPage();

const initAuth = (authService: AuthService) => (): Observable<boolean> =>
  authService.checkAuthStatus().pipe(take(1));

const placement = NgxPopperjsPlacements.RIGHT;

@NgModule({
  imports: [
    CoreRoutingModule,
    NgMapsCoreModule,
    NgxPopperjsModule.forRoot({ placement }),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
  ],
  exports: [CoreRoutingModule, NgMapsCoreModule, TranslateModule],
  providers: [
    CookieService,
    CssService,
    DashboardService,
    DialogService,
    EssService,
    ExcelService,
    ForecastService,
    GlobalAlertService,
    GoogleService,
    GridProgramService,
    I18nService,
    IconsService,
    InlineEditService,
    TreeService,
    LoadProfileService,
    LoaderService,
    LocalesService,
    LoginRedirectService,
    ProjectService,
    ResourceService,
    ResultService,
    RouterService,
    ScenarioService,
    ScenarioEventsService,
    SidenavService,
    SolarPvService,
    SiteConstraintService,
    TariffService,
    TimezonesService,
    UserService,
    AuthService,
    {
      provide: APP_INITIALIZER,
      useFactory: initIcon,
      deps: [IconsService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initLocales,
      deps: [LocalesService],
      multi: true,
    },
    // Commented out to prevent redirect loops with old auth system
    // {
    //   provide: APP_INITIALIZER,
    //   useFactory: initLoginRedirect,
    //   deps: [LoginRedirectService],
    //   multi: true,
    // },
    {
      provide: APP_INITIALIZER,
      useFactory: initGoogleApiKey,
      deps: [GoogleService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initRouterCheck,
      deps: [RouterService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initTimezones,
      deps: [TimezonesService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initAuth,
      deps: [AuthService],
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthHttpInterceptor,
      multi: true,
    },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: LoaderInterceptor, multi: true },
    {
      provide: GOOGLE_MAPS_API_CONFIG,
      useClass: GoogleService,
    },
    {
      provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS,
      useValue: {
        useUtc: false,
        strict: true,
      },
    },
    { provide: MAT_DATE_FORMATS, useValue: MAT_MOMENT_DATE_FORMATS },
    { provide: NOC_ANALYTICS, useValue: analytics },
    {
      provide: STEPPER_GLOBAL_OPTIONS,
      useValue: { displayDefaultIndicatorType: false },
    },
  ],
})
export class CoreModule {
  // Make sure CoreModule is imported only by one NgModule the AppModule
  constructor(@Optional() @SkipSelf() parentModule: CoreModule) {
    if (parentModule) {
      throw new Error('CoreModule is already loaded. Import only in AppModule');
    }
  }
}
